import { Controller } from "@hotwired/stimulus";
import {
  errorDetail,
  formatDate,
  getAllJSON,
  getJSON,
  listResults,
  sendJSON,
} from "../api.js";

// Reklamacje: lista + kaskadowe zgłoszenie (klient: zamówienie → pozycja → powód ze
// słownika → preferencja) + rozstrzyganie (personel, staffValue).
// ZERO wolnego tekstu klienta (reklamacje 2.0) — żadnego textarea/inputu opisu.
// 0-PII: operujemy na kodach pozycji (order_item_code), nigdy na danych leada.
export default class extends Controller {
  static targets = [
    "rows",
    "status",
    "form",
    "orderSelect",
    "itemSelect",
    "reasonSelect",
    "formError",
    "modal",
    "modalTitle",
    "modalBody",
    "modalError",
    "confirmBtn",
    "candidates",
    "resolveNote",
    "resolveNoteError",
    // ins-slots#7: slot complaint — zwolnienie slotu po rozstrzygnięciu.
    "releaseSlotSection",  // cały blok; hidden gdy complaint.sale_mode != "slot"
    "releaseSlotWarning",  // ostrzeżenie dla rozstrzygnięcia "replaced" (lead wadliwy)
    "releaseSlotCheck",    // checkbox; .checked → release_slot: true w POST
  ];
  static values = {
    url: String, // /api/complaints/ (może nieść ?status= — filtr server-side)
    ordersUrl: String, // /api/orders/ (kaskada pobiera ?complaintable=1)
    reasonsUrl: String, // /api/complaint-reasons/ (słownik powodów; klient widzi aktywne)
    staff: Boolean,
    management: Boolean, // panel personelu: rozstrzyganie przez modal potwierdzenia + wybór zamiennika
  };

  static RESOLUTION_LABEL = {
    replaced: "Wymieniono",
    credited: "Zwrot środków (kredyt)",
    rejected: "Odrzucona",
  };

  connect() {
    this.load();
    this.#loadOrders();
    this.#loadReasons();
  }

  async load() {
    let data;
    try {
      data = await getJSON(this.urlValue);
    } catch {
      this.#status("Nie udało się załadować reklamacji. Odśwież stronę.");
      return;
    }
    const complaints = listResults(data);
    this.rowsTarget.textContent = "";
    if (!complaints.length) {
      this.#status(
        this.managementValue ? "Brak otwartych reklamacji." : "Brak zgłoszeń.",
      );
      return;
    }
    this.#status(null);
    const template = document.getElementById("complaint-row-template");
    for (const complaint of complaints) {
      const row = template.content.firstElementChild.cloneNode(true);
      row.querySelector('[data-field="code"]').textContent =
        complaint.order_item_code;
      // Powód ze słownika: pokazuj nazwę (reason_name), nie surowe id FK.
      row.querySelector('[data-field="reason"]').textContent =
        complaint.reason_name ?? String(complaint.reason ?? "—");
      const preferenceCell = row.querySelector('[data-field="preference"]');
      if (preferenceCell) {
        preferenceCell.textContent =
          complaint.preferred_resolution_label ?? "—";
      }
      row.querySelector('[data-field="created"]').textContent = formatDate(
        complaint.created,
      );

      // Reklamacja ma tylko dwa stany: otwarta albo rozstrzygnięta. Stan pośredni
      // („oczekuje na podmianę") wycięto świadomie — przy braku zamiennika klient
      // dostaje zwrot od razu, zamiast najpierw zobaczyć obietnicę nowego leada.
      const open = complaint.status === "open";
      const badge = row.querySelector('[data-field="badge"]');
      if (open) {
        badge.className = "ls-badge ls-badge--warm";
        badge.textContent = "otwarta";
      } else {
        badge.className = "ls-badge ls-badge--sent";
        badge.textContent = "rozstrzygnięta";
      }

      const resolutionCell = row.querySelector('[data-field="resolution"]');
      if (!open) {
        resolutionCell.textContent =
          this.constructor.RESOLUTION_LABEL[complaint.resolution] ??
          complaint.resolution;
        if (complaint.resolution_note) {
          const noteEl = document.createElement("span");
          noteEl.className = "ls-text--muted";
          noteEl.style.cssText = "font-size:12px;display:block;margin-top:3px;";
          const noteLabel = document.createElement("span");
          noteLabel.textContent = "Uzasadnienie: ";
          const noteText = document.createElement("span");
          noteText.textContent = complaint.resolution_note;
          noteEl.append(noteLabel, noteText);
          resolutionCell.appendChild(noteEl);
        }
      } else if (this.staffValue) {
        for (const [resolution, label] of Object.entries(
          this.constructor.RESOLUTION_LABEL,
        )) {
          const btn = document.createElement("button");
          btn.type = "button";
          btn.className = "ls-btn ls-btn--ghost ls-btn--sm";
          btn.style.cssText =
            "font-size:12px;padding:6px 10px;margin-right:6px;";
          btn.textContent = label;
          // Panel personelu: modal potwierdzenia (rozstrzygnięcie nieodwracalne). Strona klienta
          // (gdyby staff ją otworzył) zostaje przy bezpośrednim resolve — brak modala w DOM.
          btn.setAttribute(
            "data-action",
            this.managementValue
              ? "complaints#openResolve"
              : "complaints#resolve",
          );
          btn.setAttribute("data-complaints-id-param", complaint.id);
          btn.setAttribute("data-complaints-resolution-param", resolution);
          btn.setAttribute(
            "data-complaints-code-param",
            complaint.order_item_code,
          );
          // ins-slots#7: forward sale_mode so openResolve can show releaseSlotSection.
          // WHY: ComplaintSerializer does not yet include sale_mode — value will be ""
          // until the backend adds the field; releaseSlotSection stays hidden in that case.
          btn.setAttribute(
            "data-complaints-sale-mode-param",
            complaint.sale_mode ?? "",
          );
          resolutionCell.appendChild(btn);
        }
      } else {
        resolutionCell.textContent = "— w rozpatrywaniu";
      }
      this.rowsTarget.appendChild(row);
    }
  }

  // Kaskada: zmiana zamówienia przeładowuje select pozycji; pozycje z otwartą
  // reklamacją są disabled z dopiskiem „(reklamacja w toku)".
  changeOrder() {
    if (!this.hasItemSelectTarget) return;
    this.itemSelectTarget.textContent = "";
    const placeholder = document.createElement("option");
    placeholder.value = "";
    placeholder.textContent = "Wybierz pozycję…";
    this.itemSelectTarget.appendChild(placeholder);
    const order = (this._orders ?? []).find(
      (candidate) => String(candidate.id) === this.orderSelectTarget.value,
    );
    for (const item of order?.items ?? []) {
      const option = document.createElement("option");
      option.value = item.id;
      option.textContent = item.complaint_open
        ? `${item.code} — ${item.category_path} (reklamacja w toku)`
        : `${item.code} — ${item.category_path}`;
      option.disabled = Boolean(item.complaint_open);
      this.itemSelectTarget.appendChild(option);
    }
  }

  async create(event) {
    event?.preventDefault?.();
    // Walidacja HTML (required na selectach i radio preferencji) — bez preferencji nie ma POST.
    if (this.hasFormTarget && !this.formTarget.reportValidity()) return;
    const orderItem = this.itemSelectTarget.value;
    const reason = this.reasonSelectTarget.value;
    const preference = this.element.querySelector(
      'input[name="preferred_resolution"]:checked',
    );
    if (!orderItem || !reason || !preference) {
      this.#formError(
        "Wybierz pozycję, powód i preferowane rozstrzygnięcie.",
      );
      return;
    }
    // Body zawiera DOKŁADNIE te trzy pola — zero wolnego tekstu klienta.
    const res = await sendJSON("POST", this.urlValue, {
      order_item: Number(orderItem),
      reason: Number(reason),
      preferred_resolution: preference.value,
    });
    if (!res.ok) {
      this.#formError(
        errorDetail(res.data, "Nie udało się zgłosić reklamacji."),
      );
      return;
    }
    this.#formError(null);
    if (this.hasFormTarget) this.formTarget.reset();
    this.changeOrder();
    await this.load();
  }

  async resolve(event) {
    const res = await sendJSON(
      "POST",
      `${this.#base()}${event.params.id}/resolve/`,
      { resolution: event.params.resolution },
    );
    if (!res.ok) {
      this.#formError(
        errorDetail(res.data, "Nie udało się rozstrzygnąć reklamacji."),
      );
      return;
    }
    await this.load();
  }

  // Panel personelu: otwórz modal potwierdzenia. Dla 'replaced' pobierz kandydatów (0-PII) do wyboru.
  async openResolve(event) {
    this.pending = {
      id: event.params.id,
      resolution: event.params.resolution,
      // ins-slots#7: forward sale_mode so we know whether to show the slot-release section.
      // ComplaintSerializer does not yet return sale_mode → value will be "" → section stays hidden.
      saleMode: event.params.saleMode ?? "",
    };
    const label =
      this.constructor.RESOLUTION_LABEL[this.pending.resolution] ??
      this.pending.resolution;
    if (this.hasModalTitleTarget)
      this.modalTitleTarget.textContent = `Rozstrzygnięcie: ${label}`;
    if (this.hasModalErrorTarget) this.modalErrorTarget.hidden = true;
    if (this.hasResolveNoteTarget) this.resolveNoteTarget.value = "";
    if (this.hasResolveNoteErrorTarget) this.resolveNoteErrorTarget.hidden = true;
    if (this.hasCandidatesTarget) this.candidatesTarget.textContent = "";

    // ins-slots#7: slot-release block — visible only for slot-mode complaints.
    // WHY guard on saleMode==="slot": showing the checkbox for exclusivity complaints would
    // mislead staff (backend ignores release_slot for non-slot items, but the UI must not lie).
    const isSlot = this.pending.saleMode === "slot";
    if (this.hasReleaseSlotSectionTarget) {
      this.releaseSlotSectionTarget.hidden = !isSlot;
    }
    // Reset checkbox state each time the modal opens.
    if (this.hasReleaseSlotCheckTarget) {
      this.releaseSlotCheckTarget.checked = false;
    }
    // releaseSlotWarning: shown only for slot + "replaced" resolution — lead was defective,
    // releasing it returns it to the pool for another org to buy again (conscious staff decision).
    if (this.hasReleaseSlotWarningTarget) {
      this.releaseSlotWarningTarget.hidden = !(isSlot && this.pending.resolution === "replaced");
    }
    this.selectedCandidate = null;

    if (this.pending.resolution === "replaced") {
      // Zamiennik wymagany — potwierdzenie zablokowane do czasu wyboru.
      if (this.hasConfirmBtnTarget) this.confirmBtnTarget.disabled = true;
      if (this.hasModalBodyTarget) {
        this.modalBodyTarget.textContent = `Wybierz listing-zamiennik dla pozycji ${event.params.code}. Rozstrzygnięcie jest nieodwracalne.`;
      }
      await this.#loadCandidates(this.pending.id);
    } else {
      if (this.hasConfirmBtnTarget) this.confirmBtnTarget.disabled = false;
      if (this.hasModalBodyTarget) {
        this.modalBodyTarget.textContent = `Potwierdź rozstrzygnięcie „${label}" dla pozycji ${event.params.code}. Operacja jest nieodwracalna.`;
      }
    }
    if (this.hasModalTarget) this.modalTarget.hidden = false;
  }

  selectCandidate(event) {
    this.selectedCandidate = event.currentTarget.value;
    if (this.hasConfirmBtnTarget) this.confirmBtnTarget.disabled = false;
  }

  async confirmResolve() {
    if (!this.pending) return;
    // Walidacja notatki — wymagana przy każdym rozstrzygnięciu.
    const note = this.hasResolveNoteTarget
      ? this.resolveNoteTarget.value.trim()
      : "";
    if (!note) {
      this.#resolveNoteError("Notatka rozstrzygnięcia jest wymagana.");
      return;
    }
    if (this.hasResolveNoteErrorTarget) this.resolveNoteErrorTarget.hidden = true;
    const body = { resolution: this.pending.resolution, resolution_note: note };
    if (this.pending.resolution === "replaced" && this.selectedCandidate) {
      // Zamiennik wybrany → dołącz do body. Bez wyboru serwis sam szuka kandydata,
      // a gdy żadnego nie ma — zwraca środki do portfela klienta.
      body.replacement_listing = Number(this.selectedCandidate);
    }
    // ins-slots#7: include release_slot only when the slot-release section is visible.
    // WHY conditional: the pre-slots resolve tests pin the exact payload (money-adjacent
    // endpoint); adding release_slot to every resolve would break those contracts.
    // The section is visible only for slot complaints (set in openResolve), so this
    // condition is equivalent to "this is a slot complaint and the UI is wired".
    // Old management fixtures have no releaseSlotSection target → condition is false → omitted.
    if (this.hasReleaseSlotSectionTarget && !this.releaseSlotSectionTarget.hidden) {
      body.release_slot = this.hasReleaseSlotCheckTarget && this.releaseSlotCheckTarget.checked;
    }
    if (this.hasConfirmBtnTarget) this.confirmBtnTarget.disabled = true;
    const res = await sendJSON(
      "POST",
      `${this.#base()}${this.pending.id}/resolve/`,
      body,
    );
    if (!res.ok) {
      // 409 = już rozstrzygnięta / konflikt zamiennika — pokaż w modalu, odśwież listę pod spodem.
      this.#modalError(
        errorDetail(res.data, "Nie udało się rozstrzygnąć reklamacji."),
      );
      if (this.hasConfirmBtnTarget) this.confirmBtnTarget.disabled = false;
      await this.load();
      return;
    }
    this.closeModal();
    await this.load();
  }

  closeModal() {
    this.pending = null;
    this.selectedCandidate = null;
    if (this.hasModalTarget) this.modalTarget.hidden = true;
  }

  // Klik wewnątrz modala nie może zamykać overlaya (który nasłuchuje click->closeModal).
  stop(event) {
    event.stopPropagation();
  }

  // --- prywatne ---

  // Bazowy URL akcji na zasobie — bez ewentualnego ?status= z urlValue (filtr dotyczy tylko listy).
  #base() {
    return this.urlValue.split("?")[0];
  }

  async #loadCandidates(complaintId) {
    let candidates;
    try {
      candidates = await getJSON(
        `${this.#base()}${complaintId}/replacement-candidates/`,
      );
    } catch {
      this.#modalError("Nie udało się pobrać kandydatów na zamiennika.");
      return;
    }
    const list = listResults(candidates);
    if (!this.hasCandidatesTarget) return;
    if (!list.length) {
      // Brak kandydata = zwrot środków, nie oczekiwanie. Personel musi to wiedzieć
      // PRZED kliknięciem, bo decyzja jest ostateczna i od razu rusza pieniądze.
      this.candidatesTarget.textContent =
        "Brak zamiennika w tej podkategorii — potwierdzenie zwróci środki do portfela klienta.";
      if (this.hasConfirmBtnTarget) this.confirmBtnTarget.disabled = false;
      return;
    }
    for (const candidate of list) {
      const option = document.createElement("label");
      option.className = "ls-candidate";
      option.style.cssText =
        "display:flex;gap:10px;align-items:center;padding:8px 0;font-size:13.5px;";
      const radio = document.createElement("input");
      radio.type = "radio";
      radio.name = "replacement";
      radio.value = candidate.id;
      radio.setAttribute("data-action", "complaints#selectCandidate");
      const text = document.createElement("span");
      text.textContent = `${candidate.code} · ${candidate.subcategory} · ${candidate.voivodeship}/${candidate.county} · ${candidate.lead_type}`;
      option.append(radio, text);
      this.candidatesTarget.appendChild(option);
    }
  }

  #modalError(message) {
    if (!this.hasModalErrorTarget) return;
    this.modalErrorTarget.hidden = false;
    this.modalErrorTarget.textContent = message;
  }

  #resolveNoteError(message) {
    if (!this.hasResolveNoteErrorTarget) return;
    this.resolveNoteErrorTarget.hidden = false;
    this.resolveNoteErrorTarget.textContent = message;
  }

  async #loadOrders() {
    if (!this.hasOrderSelectTarget) return;
    try {
      // Backend zawęża do reklamowalnych (PAID/DELIVERED w oknie) i annotuje complaint_open.
      this._orders = await getAllJSON(`${this.ordersUrlValue}?complaintable=1`);
    } catch {
      this._orders = [];
      return;
    }
    for (const order of this._orders) {
      const option = document.createElement("option");
      option.value = order.id;
      option.textContent = `Zamówienie #${order.id} · ${formatDate(order.created)}`;
      this.orderSelectTarget.appendChild(option);
    }
  }

  async #loadReasons() {
    if (!this.hasReasonSelectTarget) return;
    let reasons;
    try {
      reasons = await getAllJSON(this.reasonsUrlValue); // klient dostaje tylko aktywne
    } catch {
      return;
    }
    for (const reason of reasons) {
      const option = document.createElement("option");
      option.value = reason.id;
      option.textContent = reason.name;
      this.reasonSelectTarget.appendChild(option);
    }
  }

  #formError(message) {
    if (!this.hasFormErrorTarget) return;
    this.formErrorTarget.hidden = message === null;
    if (message !== null) this.formErrorTarget.textContent = message;
  }

  #status(message) {
    if (!this.hasStatusTarget) return;
    this.statusTarget.hidden = message === null;
    if (message !== null) this.statusTarget.textContent = message;
  }
}
